import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const HeroSection: React.FC = () => {
  const { translations } = useLanguage();
  const navigate = useNavigate();

  const bannerButtons = [
    { key: 'cosmetic', label: translations.hero.banner.buttons.cosmetic },
    { key: 'pediatric', label: translations.hero.banner.buttons.pediatric },
    { key: 'general', label: translations.hero.banner.buttons.general },
    { key: 'whitening', label: translations.hero.banner.buttons.whitening },
  ];

  const handleBookClick = (treatment?: string) => {
    const bookingPath = treatment
      ? `/kontakt?booking=true&treatment=${treatment}`
      : '/kontakt?booking=true';
    navigate(bookingPath);

    // Smooth scroll to booking form if already on kontakt page
    const element = document.getElementById('booking-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <section className="relative min-h-[500px] md:min-h-[600px] flex items-center text-white pt-32 overflow-hidden">
        <div
          className="absolute inset-0 bg-navy opacity-75 z-0"
          style={{
            backgroundImage: "url('/images/SeaDent Palma Teaser Header.jpg')",
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundBlendMode: 'overlay'
          }}
          aria-hidden="true"
        ></div>
        <div className="container-custom relative z-10 py-12">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-white text-shadow-lg leading-tight">
              {translations.hero.title}
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-white opacity-90 text-shadow-md">
              {translations.hero.subtitle}
            </p>
            {translations.hero.teaser && (
              <p className="mb-8 text-lg text-white opacity-80 text-shadow-md hidden md:block">
                {translations.hero.teaser}
              </p>
            )}
            <button
              onClick={() => handleBookClick()}
              className="btn btn-primary transform hover:scale-105 transition-all text-lg px-8 py-3 shadow-xl"
            >
              {translations.hero.cta}
            </button>
          </div>
        </div>
      </section>

      {/* New Modern Banner Section */}
      <section className="bg-white py-16 md:py-24 border-b border-gray-100">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-left space-y-6">
              <h2 className="text-4xl md:text-5xl font-bold text-navy leading-tight">
                {translations.hero.banner.title.split(',').map((part, i) => (
                  <span key={i} className="block">
                    {part.trim()}
                    {i === 0 && <span className="text-light-blue">.</span>}
                  </span>
                ))}
              </h2>
              <p className="text-xl text-gray-500 font-medium italic">
                {translations.hero.banner.subtitle}
              </p>
            </div>

            <div className="space-y-4">
              {bannerButtons.map((btn) => (
                <div
                  key={btn.key}
                  className="flex rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all transform hover:-translate-y-1 group cursor-pointer border border-gray-100 h-16 md:h-20"
                  onClick={() => handleBookClick(btn.key)}
                >
                  {/* Left: Treatment Name */}
                  <div className="flex-grow bg-white px-5 md:px-8 flex items-center group-hover:bg-gray-50 transition-colors">
                    <span className="text-base md:text-xl font-bold text-navy">
                      {btn.label}
                    </span>
                  </div>

                  {/* Right: Persistently Visible Book Button - Navy by default as per SkyDental screen */}
                  <div className="bg-navy text-white px-4 md:px-8 flex items-center justify-center font-bold tracking-wider uppercase text-[10px] md:text-sm group-hover:bg-light-blue transition-all duration-300 min-w-[100px] md:min-w-[140px]">
                    <span className="flex items-center">
                      {translations.common.bookNow || 'Book'}
                      <svg className="w-3 h-3 md:w-4 md:h-4 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HeroSection;
